import React, { useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Calendar, FileText, Download } from 'lucide-react';

export default function NoticeBoard() {
  const [notices, setNotices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API_BASE_URL}/GetNotice`)
      .then(res => {
        const raw = Array.isArray(res.data) ? res.data : [];
        const sorted = raw.sort((a, b) => (b._id || '').localeCompare(a._id || ''));
        setNotices(sorted);
      })
      .catch(err => {
        console.error('Error fetching notices:', err);
        setNotices([]);
      });
  }, []);

  const handleOpenPdf = (link) => {
    navigate(`/Documentation?file=${encodeURIComponent(link)}`);
  };

  const getNoticeDate = (id) => {
    try {
      if (!id || id.length < 8) return 'Recent';
      const timestamp = parseInt(id.substring(0, 8), 16) * 1000;
      return new Date(timestamp).toLocaleDateString('default', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch (e) {
      return 'Recent';
    }
  };

  const safeNotices = Array.isArray(notices) ? notices : [];
  const processedNotices = safeNotices.map((item) => {
    return {
      id: item._id,
      link: item.Link,
      title: item.Heading || 'Official Notice',
      description: item.Description || '',
      category: item.Category || 'General',
      date: getNoticeDate(item._id)
    };
  });

  const filteredNotices = processedNotices.filter(notice => {
    const matchesSearch = notice.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          notice.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          notice.category.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  return (
    <div style={{ minHeight: '90vh', background: 'var(--bg-academics)', paddingTop: '120px', paddingBottom: '60px', fontFamily: 'Quicksand', position: 'relative', overflow: 'hidden' }}>
      {/* Playful floating illustrations background details (scrapbook style doodles) */}
      <div style={{ position: 'absolute', top: '5%', left: '3%', fontSize: '2.5rem', opacity: 0.15 }} className="cloud-float">☁️</div>
      <div style={{ position: 'absolute', top: '8%', right: '8%', fontSize: '3rem', opacity: 0.15 }} className="sun-spin">☀️</div>
      <div style={{ position: 'absolute', top: '15%', left: '10%', fontSize: '2rem', opacity: 0.15 }} className="float-slow">✏️</div>
      <div style={{ position: 'absolute', top: '20%', right: '15%', fontSize: '2.5rem', opacity: 0.12 }} className="cloud-float">🎈</div>
      <div style={{ position: 'absolute', top: '30%', left: '4%', fontSize: '2rem', opacity: 0.2 }} className="plane-float">✈️</div>
      <div style={{ position: 'absolute', top: '40%', right: '6%', fontSize: '2.5rem', opacity: 0.12 }} className="float-slow">🎨</div>
      <div style={{ position: 'absolute', top: '50%', left: '8%', fontSize: '2rem', opacity: 0.15 }} className="cloud-float">✨</div>
      <div style={{ position: 'absolute', top: '58%', right: '12%', fontSize: '2.2rem', opacity: 0.15 }} className="float-slow">✏️</div>
      <div style={{ position: 'absolute', top: '65%', left: '2%', fontSize: '2.8rem', opacity: 0.15 }} className="plane-float">🪁</div>
      <div style={{ position: 'absolute', top: '72%', right: '4%', fontSize: '2rem', opacity: 0.12 }}>📚</div>
      <div style={{ position: 'absolute', top: '80%', left: '6%', fontSize: '2.5rem', opacity: 0.15 }} className="cloud-float">🌈</div>
      <div style={{ position: 'absolute', top: '88%', right: '10%', fontSize: '2.2rem', opacity: 0.15 }} className="float-slow">🧸</div>
      <div style={{ position: 'absolute', bottom: '5%', left: '3%', fontSize: '2.5rem', opacity: 0.18 }} className="sun-spin">☀️</div>
      <div style={{ position: 'absolute', bottom: '8%', right: '7%', fontSize: '2rem', opacity: 0.15 }} className="cloud-float">☁️</div>
      <div style={{ position: 'absolute', bottom: '12%', left: '12%', fontSize: '2.3rem', opacity: 0.15 }} className="float-slow">🍎</div>
      <div style={{ position: 'absolute', bottom: '2%', right: '3%', fontSize: '2rem', opacity: 0.15 }}>🖍️</div>

      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 10 }}>
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{ textAlign: 'center', marginBottom: '40px' }}
        >
          <h1 style={{ fontSize: '2.5rem', color: 'var(--text-primary)', marginBottom: '8px', fontFamily: 'Poppins', fontWeight: 800 }}>Notice Board</h1>
          <p style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Stay updated with the latest school circulars, schedules, and holidays.</p>
        </motion.div>

        {/* Filters and Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          style={filterContainerStyle}
        >
          <div style={searchWrapperStyle}>
            <Search size={18} style={{ color: 'var(--text-muted)' }} />
            <input 
              type="text" 
              placeholder="Search notices..." 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
              style={searchInputStyle} 
            />
          </div>
          
        </motion.div>

        {/* Timeline List */}
        {filteredNotices.length === 0 ? (
          <div style={emptyStateStyle}>
            <FileText size={48} style={{ color: 'var(--text-muted)', marginBottom: '16px' }} />
            <h3 style={{ color: 'var(--text-secondary)', fontFamily: 'Poppins' }}>No Notices Found</h3>
            <p style={{ color: 'var(--text-muted)' }}>Try adjusting your search criteria.</p>
          </div>
        ) : (
          <div style={timelineStyle}>
            {filteredNotices.map((notice, index) => (
              <motion.div 
                key={notice.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                style={timelineItemStyle}
              >
                {/* Timeline node dot */}
                <div style={timelineDotStyle(notice.category)} />
                
                {/* Notice Card */}
                <div style={noticeCardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                    <div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span style={badgeStyle(notice.category)}>{notice.category}</span>
                        {index < 2 && (
                          <span style={{ fontSize: '0.72rem', fontWeight: 800, backgroundColor: 'var(--accent-coral)', color: '#ffffff', padding: '2px 8px', borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>New</span>
                        )}
                      </div>
                      <h3 style={{ fontSize: '1.25rem', color: 'var(--text-primary)', margin: '8px 0 4px', fontWeight: 700, fontFamily: 'Poppins' }}>
                        {notice.title}
                      </h3>
                      {notice.description && (
                        <p style={{ fontSize: '0.92rem', color: 'var(--text-secondary)', margin: '6px 0 10px', fontWeight: 600, lineHeight: '1.5' }}>
                          {notice.description}
                        </p>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: 600 }}>
                        <Calendar size={14} />
                        <span>Posted on {notice.date}</span>
                      </div>
                    </div>

                    {notice.link && (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button 
                          onClick={() => handleOpenPdf(notice.link)} 
                          className="btn-premium btn-premium-primary"
                          style={{ height: '36px', padding: '0 16px', fontSize: '0.85rem', borderRadius: '10px' }}
                        >
                          <FileText size={14} /> Open
                        </button>
                        <a 
                          href={notice.link.replace('/preview', '/export?format=pdf')} 
                          download 
                          className="btn-premium btn-premium-secondary"
                          style={{ height: '36px', width: '36px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '10px' }}
                        >
                          <Download size={14} />
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

// Inline Styles
const filterContainerStyle = {
  backgroundColor: '#ffffff',
  padding: '20px',
  borderRadius: 'var(--border-radius-card)',
  boxShadow: 'var(--shadow-md)',
  border: '1px solid var(--border-color)',
  marginBottom: '32px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px'
};

const searchWrapperStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--border-radius-form)',
  padding: '0 16px',
  backgroundColor: 'var(--bg-color)'
};

const searchInputStyle = {
  border: 'none',
  background: 'transparent',
  padding: '12px 0',
  width: '100%',
  outline: 'none',
  fontSize: '0.95rem',
  color: 'var(--text-primary)',
  fontFamily: 'Quicksand',
  fontWeight: 600
};



const emptyStateStyle = {
  textAlign: 'center',
  padding: '48px 0',
  backgroundColor: '#ffffff',
  borderRadius: 'var(--border-radius-card)',
  border: '1px solid var(--border-color)',
  boxShadow: 'var(--shadow-md)'
};

const timelineStyle = {
  position: 'relative',
  paddingLeft: '32px',
  borderLeft: '3px dashed var(--primary-color)',
  marginLeft: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px'
};

const timelineItemStyle = {
  position: 'relative'
};

const timelineDotStyle = (cat) => {
  let color = 'var(--primary-color)';
  if (cat === 'Examinations') color = 'var(--accent-coral)';
  if (cat === 'Holidays') color = 'var(--secondary-color)';
  if (cat === 'Fees') color = 'var(--accent-green)';
  return {
    position: 'absolute',
    left: '-42px',
    top: '24px',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    backgroundColor: color,
    border: '4px solid var(--bg-color)',
    boxShadow: 'var(--shadow-sm)'
  };
};

const noticeCardStyle = {
  backgroundColor: '#ffffff',
  padding: '24px',
  borderRadius: 'var(--border-radius-card)',
  border: '1px solid var(--border-color)',
  boxShadow: 'var(--shadow-md)',
  transition: 'transform var(--transition-normal)'
};

const badgeStyle = (cat) => {
  let bg = 'rgba(96, 165, 250, 0.1)';
  let color = 'var(--primary-dark)';

  if (cat === 'Examinations') {
    bg = 'rgba(251, 113, 133, 0.1)';
    color = '#f43f5e';
  } else if (cat === 'Holidays') {
    bg = 'rgba(250, 204, 21, 0.15)';
    color = '#d97706';
  } else if (cat === 'Fees') {
    bg = 'rgba(74, 222, 128, 0.1)';
    color = '#16a34a';
  }

  return {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '9999px',
    backgroundColor: bg,
    color: color,
    fontSize: '0.75rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };
};

// Add standard timeline hover triggers
const timelineStyles = document.createElement('style');
timelineStyles.innerHTML = `
  div[style*="transition"]:hover {
    transform: translateY(-4px) !important;
    border-color: var(--primary-color) !important;
  }
`;
document.head.appendChild(timelineStyles);
